// SEO snippet generators (robots.txt, meta tags, Open Graph, JSON-LD schema,
// FAQ schema, sitemap.xml, .htaccess). Pure, browser-local, config-driven.

export type SeoGenVariant =
  | 'robots-txt-generator'
  | 'meta-tag-generator'
  | 'open-graph-generator'
  | 'schema-markup-generator'
  | 'faq-schema-generator'
  | 'sitemap-generator'
  | 'htaccess-generator';

export type SeoGenFieldKind = 'text' | 'textarea' | 'select';

export type SeoGenField = {
  key: string;
  label: string;
  kind: SeoGenFieldKind;
  default: string;
  full?: boolean;
  rows?: number;
  placeholder?: string;
  options?: { value: string; label: string }[];
};

export type SeoGenConfig = {
  badge: string;
  heading: string;
  subtitle: string;
  outputRows: number;
  fields: SeoGenField[];
  generate: (values: Record<string, string>) => string;
};

function esc(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function lines(value: string): string[] {
  return value
    .split(/\r?\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

function jsonLd(data: Record<string, unknown>): string {
  return `<script type="application/ld+json">\n${JSON.stringify(data, null, 2)}\n</script>`;
}

// --- individual generators -------------------------------------------------

function generateRobotsTxt(v: Record<string, string>): string {
  const out: string[] = [`User-agent: ${(v.userAgent || '*').trim() || '*'}`];
  for (const path of lines(v.disallow || '')) out.push(`Disallow: ${path}`);
  for (const path of lines(v.allow || '')) out.push(`Allow: ${path}`);
  if (!(v.disallow || '').trim() && !(v.allow || '').trim()) out.push('Disallow:');
  const sitemap = (v.sitemap || '').trim();
  if (sitemap) out.push('', `Sitemap: ${sitemap}`);
  return out.join('\n');
}

function generateMetaTags(v: Record<string, string>): string {
  const tags: string[] = [];
  const title = (v.title || '').trim();
  const description = (v.description || '').trim();
  if (title) tags.push(`<title>${esc(title)}</title>`);
  if (description) tags.push(`<meta name="description" content="${esc(description)}" />`);
  if ((v.keywords || '').trim())
    tags.push(`<meta name="keywords" content="${esc(v.keywords.trim())}" />`);
  if ((v.author || '').trim())
    tags.push(`<meta name="author" content="${esc(v.author.trim())}" />`);
  if (title) tags.push(`<meta property="og:title" content="${esc(title)}" />`);
  if (description) tags.push(`<meta property="og:description" content="${esc(description)}" />`);
  tags.push('<meta property="og:type" content="website" />');
  if ((v.url || '').trim()) tags.push(`<meta property="og:url" content="${esc(v.url.trim())}" />`);
  if ((v.image || '').trim())
    tags.push(`<meta property="og:image" content="${esc(v.image.trim())}" />`);
  tags.push('<meta name="twitter:card" content="summary_large_image" />');
  if (title) tags.push(`<meta name="twitter:title" content="${esc(title)}" />`);
  if (description) tags.push(`<meta name="twitter:description" content="${esc(description)}" />`);
  if ((v.image || '').trim())
    tags.push(`<meta name="twitter:image" content="${esc(v.image.trim())}" />`);
  return tags.join('\n');
}

function generateOpenGraph(v: Record<string, string>): string {
  const tags: string[] = [];
  const title = (v.title || '').trim();
  const description = (v.description || '').trim();
  const type = (v.type || 'website').trim() || 'website';
  if (title) tags.push(`<meta property="og:title" content="${esc(title)}" />`);
  if (description) tags.push(`<meta property="og:description" content="${esc(description)}" />`);
  tags.push(`<meta property="og:type" content="${esc(type)}" />`);
  if ((v.url || '').trim()) tags.push(`<meta property="og:url" content="${esc(v.url.trim())}" />`);
  if ((v.image || '').trim())
    tags.push(`<meta property="og:image" content="${esc(v.image.trim())}" />`);
  if ((v.siteName || '').trim())
    tags.push(`<meta property="og:site_name" content="${esc(v.siteName.trim())}" />`);
  if ((v.locale || '').trim())
    tags.push(`<meta property="og:locale" content="${esc(v.locale.trim())}" />`);
  tags.push('<meta name="twitter:card" content="summary_large_image" />');
  if ((v.twitterHandle || '').trim()) {
    const handle = v.twitterHandle.trim().startsWith('@')
      ? v.twitterHandle.trim()
      : `@${v.twitterHandle.trim()}`;
    tags.push(`<meta name="twitter:site" content="${esc(handle)}" />`);
  }
  if (title) tags.push(`<meta name="twitter:title" content="${esc(title)}" />`);
  if (description) tags.push(`<meta name="twitter:description" content="${esc(description)}" />`);
  if ((v.image || '').trim())
    tags.push(`<meta name="twitter:image" content="${esc(v.image.trim())}" />`);
  return tags.join('\n');
}

function generateSchemaMarkup(v: Record<string, string>): string {
  const type = (v.type || 'Organization').trim() || 'Organization';
  const data: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': type,
  };
  const name = (v.name || '').trim();
  if (name) {
    // Articles use headline as the primary title property.
    if (type === 'Article' || type === 'BlogPosting' || type === 'NewsArticle') {
      data.headline = name;
    } else {
      data.name = name;
    }
  }
  if ((v.url || '').trim()) data.url = v.url.trim();
  if ((v.description || '').trim()) data.description = v.description.trim();
  if ((v.image || '').trim()) {
    data.image = v.image.trim();
    if (type === 'Organization' || type === 'LocalBusiness') data.logo = v.image.trim();
  }
  // Extra properties as "key: value" lines (values that look like JSON are parsed).
  for (const line of lines(v.extraProps || '')) {
    const idx = line.indexOf(':');
    if (idx === -1) continue;
    const key = line.slice(0, idx).trim();
    const raw = line.slice(idx + 1).trim();
    if (!key) continue;
    let value: unknown = raw;
    if (/^-?\d+(\.\d+)?$/.test(raw)) {
      value = Number(raw);
    } else if (raw === 'true' || raw === 'false') {
      value = raw === 'true';
    } else if (raw.startsWith('{') || raw.startsWith('[')) {
      try {
        value = JSON.parse(raw);
      } catch {
        value = raw;
      }
    }
    data[key] = value;
  }
  return jsonLd(data);
}

function generateFaqSchema(v: Record<string, string>): string {
  const mainEntity: Record<string, unknown>[] = [];
  for (const line of lines(v.faqs || '')) {
    const idx = line.indexOf('|');
    if (idx === -1) continue;
    const question = line.slice(0, idx).trim();
    const answer = line.slice(idx + 1).trim();
    if (!question || !answer) continue;
    mainEntity.push({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    });
  }
  return jsonLd({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity,
  });
}

function generateSitemap(v: Record<string, string>): string {
  const changefreq = (v.changefreq || '').trim();
  const priority = (v.priority || '').trim();
  const lastmod = (v.lastmod || '').trim();
  const urls = lines(v.urls || '');
  const body = urls
    .map((loc) => {
      const parts = [`    <loc>${esc(loc)}</loc>`];
      if (lastmod) parts.push(`    <lastmod>${esc(lastmod)}</lastmod>`);
      if (changefreq) parts.push(`    <changefreq>${esc(changefreq)}</changefreq>`);
      if (priority) parts.push(`    <priority>${esc(priority)}</priority>`);
      return `  <url>\n${parts.join('\n')}\n  </url>`;
    })
    .join('\n');
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${body}\n</urlset>`;
}

function generateHtaccess(v: Record<string, string>): string {
  const out: string[] = [];
  if ((v.forceHttps || 'no') === 'yes') {
    out.push(
      '# Force HTTPS',
      'RewriteEngine On',
      'RewriteCond %{HTTPS} off',
      'RewriteRule ^(.*)$ https://%{HTTP_HOST}%{REQUEST_URI} [L,R=301]',
      '',
    );
  }
  if ((v.www || 'none') === 'force-www') {
    if (!out.includes('RewriteEngine On')) out.push('RewriteEngine On');
    out.push(
      '# Force www',
      'RewriteCond %{HTTP_HOST} !^www\\. [NC]',
      'RewriteRule ^(.*)$ https://www.%{HTTP_HOST}%{REQUEST_URI} [L,R=301]',
      '',
    );
  } else if ((v.www || 'none') === 'force-non-www') {
    if (!out.includes('RewriteEngine On')) out.push('RewriteEngine On');
    out.push(
      '# Force non-www',
      'RewriteCond %{HTTP_HOST} ^www\\.(.*)$ [NC]',
      'RewriteRule ^(.*)$ https://%1%{REQUEST_URI} [L,R=301]',
      '',
    );
  }
  if ((v.errorDocument404 || '').trim()) {
    out.push(`ErrorDocument 404 ${v.errorDocument404.trim()}`, '');
  }
  if ((v.gzip || 'no') === 'yes') {
    out.push(
      '# Enable gzip compression',
      '<IfModule mod_deflate.c>',
      '  AddOutputFilterByType DEFLATE text/html text/plain text/css application/javascript application/json image/svg+xml',
      '</IfModule>',
      '',
    );
  }
  if ((v.cacheStatic || 'no') === 'yes') {
    out.push(
      '# Cache static assets',
      '<IfModule mod_expires.c>',
      '  ExpiresActive On',
      '  ExpiresByType image/jpeg "access plus 1 year"',
      '  ExpiresByType image/png "access plus 1 year"',
      '  ExpiresByType image/webp "access plus 1 year"',
      '  ExpiresByType text/css "access plus 1 month"',
      '  ExpiresByType application/javascript "access plus 1 month"',
      '</IfModule>',
      '',
    );
  }
  return out.join('\n').trimEnd() || '# Choose options above to generate .htaccess rules.';
}

// --- config registry -------------------------------------------------------

export const SEOGEN_CONFIG: Record<SeoGenVariant, SeoGenConfig> = {
  'robots-txt-generator': {
    badge: 'robots.txt',
    heading: 'Generate a robots.txt file',
    subtitle: 'Fill in the fields and copy the generated output. Everything runs in your browser.',
    outputRows: 12,
    generate: generateRobotsTxt,
    fields: [
      { key: 'userAgent', label: 'User-agent', kind: 'text', default: '*', full: true },
      {
        key: 'disallow',
        label: 'Disallow (one path per line)',
        kind: 'textarea',
        default: '/admin/\n/api/',
        rows: 3,
        full: true,
      },
      {
        key: 'allow',
        label: 'Allow (one path per line)',
        kind: 'textarea',
        default: '',
        rows: 2,
        full: true,
      },
      {
        key: 'sitemap',
        label: 'Sitemap URL',
        kind: 'text',
        default: 'https://example.com/sitemap.xml',
        full: true,
      },
    ],
  },
  'meta-tag-generator': {
    badge: 'Meta tags',
    heading: 'Generate SEO meta tags',
    subtitle: 'Fill in the fields and copy the generated output. Everything runs in your browser.',
    outputRows: 12,
    generate: generateMetaTags,
    fields: [
      { key: 'title', label: 'Title', kind: 'text', default: 'My Page Title', full: true },
      {
        key: 'description',
        label: 'Description',
        kind: 'textarea',
        default: 'A short, compelling description of the page.',
        rows: 2,
        full: true,
      },
      { key: 'url', label: 'Page URL', kind: 'text', default: 'https://example.com/', full: true },
      {
        key: 'image',
        label: 'OG image URL',
        kind: 'text',
        default: 'https://example.com/og.jpg',
        full: true,
      },
      { key: 'keywords', label: 'Keywords', kind: 'text', default: '' },
      { key: 'author', label: 'Author', kind: 'text', default: '' },
    ],
  },
  'open-graph-generator': {
    badge: 'Open Graph',
    heading: 'Generate Open Graph & Twitter tags',
    subtitle: 'Fill in the fields and copy the generated output. Everything runs in your browser.',
    outputRows: 12,
    generate: generateOpenGraph,
    fields: [
      { key: 'title', label: 'Title', kind: 'text', default: 'My Page Title', full: true },
      {
        key: 'description',
        label: 'Description',
        kind: 'textarea',
        default: 'A short, compelling description of the page.',
        rows: 2,
        full: true,
      },
      {
        key: 'type',
        label: 'og:type',
        kind: 'select',
        default: 'website',
        options: [
          { value: 'website', label: 'website' },
          { value: 'article', label: 'article' },
          { value: 'product', label: 'product' },
          { value: 'profile', label: 'profile' },
          { value: 'video.other', label: 'video' },
        ],
      },
      { key: 'url', label: 'Page URL', kind: 'text', default: 'https://example.com/' },
      {
        key: 'image',
        label: 'Image URL',
        kind: 'text',
        default: 'https://example.com/og.jpg',
        full: true,
      },
      { key: 'siteName', label: 'Site name', kind: 'text', default: '' },
      { key: 'locale', label: 'Locale (e.g. en_US)', kind: 'text', default: '' },
      { key: 'twitterHandle', label: 'Twitter @handle', kind: 'text', default: '' },
    ],
  },
  'schema-markup-generator': {
    badge: 'JSON-LD',
    heading: 'Generate schema.org JSON-LD markup',
    subtitle: 'Fill in the fields and copy the generated output. Everything runs in your browser.',
    outputRows: 14,
    generate: generateSchemaMarkup,
    fields: [
      {
        key: 'type',
        label: 'Schema type',
        kind: 'select',
        default: 'Organization',
        options: [
          { value: 'Organization', label: 'Organization' },
          { value: 'LocalBusiness', label: 'LocalBusiness' },
          { value: 'WebSite', label: 'WebSite' },
          { value: 'Article', label: 'Article' },
          { value: 'BlogPosting', label: 'BlogPosting' },
          { value: 'Product', label: 'Product' },
          { value: 'Person', label: 'Person' },
          { value: 'Event', label: 'Event' },
        ],
      },
      { key: 'name', label: 'Name / headline', kind: 'text', default: 'Acme Inc.', full: true },
      { key: 'url', label: 'URL', kind: 'text', default: 'https://example.com/', full: true },
      {
        key: 'description',
        label: 'Description',
        kind: 'textarea',
        default: '',
        rows: 2,
        full: true,
      },
      { key: 'image', label: 'Image / logo URL', kind: 'text', default: '', full: true },
      {
        key: 'extraProps',
        label: 'Additional properties ("key: value" per line)',
        kind: 'textarea',
        default: 'telephone: +1-555-0100',
        rows: 3,
        full: true,
      },
    ],
  },
  'faq-schema-generator': {
    badge: 'FAQ JSON-LD',
    heading: 'Generate FAQPage schema',
    subtitle: 'One Q&A per line as "Question | Answer". Everything runs in your browser.',
    outputRows: 14,
    generate: generateFaqSchema,
    fields: [
      {
        key: 'faqs',
        label: 'FAQs ("Question | Answer" per line)',
        kind: 'textarea',
        default:
          'Is it free? | Yes, the tool is completely free with no sign-up.\nDoes it work offline? | Yes, everything runs locally in your browser.',
        rows: 8,
        full: true,
      },
    ],
  },
  'sitemap-generator': {
    badge: 'sitemap.xml',
    heading: 'Generate an XML sitemap',
    subtitle: 'One URL per line. Everything runs in your browser.',
    outputRows: 14,
    generate: generateSitemap,
    fields: [
      {
        key: 'urls',
        label: 'URLs (one per line)',
        kind: 'textarea',
        default: 'https://example.com/\nhttps://example.com/about\nhttps://example.com/contact',
        rows: 8,
        full: true,
      },
      {
        key: 'changefreq',
        label: 'Change frequency',
        kind: 'select',
        default: 'weekly',
        options: [
          { value: '', label: '(none)' },
          { value: 'always', label: 'always' },
          { value: 'hourly', label: 'hourly' },
          { value: 'daily', label: 'daily' },
          { value: 'weekly', label: 'weekly' },
          { value: 'monthly', label: 'monthly' },
          { value: 'yearly', label: 'yearly' },
          { value: 'never', label: 'never' },
        ],
      },
      { key: 'priority', label: 'Priority (0.0-1.0)', kind: 'text', default: '0.8' },
      { key: 'lastmod', label: 'Last modified (YYYY-MM-DD)', kind: 'text', default: '' },
    ],
  },
  'htaccess-generator': {
    badge: '.htaccess',
    heading: 'Generate an .htaccess file',
    subtitle: 'Pick the rules you need. Everything runs in your browser.',
    outputRows: 14,
    generate: generateHtaccess,
    fields: [
      {
        key: 'forceHttps',
        label: 'Force HTTPS',
        kind: 'select',
        default: 'yes',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
      {
        key: 'www',
        label: 'www preference',
        kind: 'select',
        default: 'none',
        options: [
          { value: 'none', label: 'Leave as-is' },
          { value: 'force-www', label: 'Force www' },
          { value: 'force-non-www', label: 'Force non-www' },
        ],
      },
      {
        key: 'errorDocument404',
        label: 'Custom 404 page (path)',
        kind: 'text',
        default: '/404.html',
      },
      {
        key: 'gzip',
        label: 'Enable gzip compression',
        kind: 'select',
        default: 'yes',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
      {
        key: 'cacheStatic',
        label: 'Cache static assets',
        kind: 'select',
        default: 'yes',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
  },
};

export function defaultSeoGenValues(variant: SeoGenVariant): Record<string, string> {
  const values: Record<string, string> = {};
  for (const field of SEOGEN_CONFIG[variant].fields) values[field.key] = field.default;
  return values;
}

// Back-compat named exports (kept for any external callers).
export { generateRobotsTxt, generateMetaTags };
